<template lang="pug">

v-dialog(
  v-model="showFormDialog"
  v-if="showFormDialog"
  class="custom-fields-form-dialog"
  width="800"
  hide-overlay
)

  div(
    class="form-content"
    v-click-outside="closeFormDialog"
  )
    div(class="form-title-container")
      span(class="form-title") {{ typeOfFieldToRenderInForm }}

    span(class="required-fields-advice") {{ $t('components.form_generator.required_fields') }}

    FormGeneratorComponent(
      :schema="schema"
      :fieldValues="formData"
      :context="context"
      formTag="customFieldsFormDialog"
    )
    LfActionsBarComponent(
      :buttons="buttons"
      @execute-action="executeAction"
      class="action-buttons"
      :showKebabMenu="false"
    )

</template>

<script lang="ts">
import { ContextName, ModuleNamespaces } from '@/store/types/storeGlobalTypes'
import { Vue, Component, Prop, Emit } from 'vue-property-decorator'
import { Getter, Mutation } from 'vuex-class'
import LfActionsBarComponent from '@/components/ActionsBar/LfActionsBarComponent.vue'
import FormGeneratorComponent from '@/components/forms/FormGenerator/FormGeneratorComponent.vue'
import { Icons } from '@/icons/icons'
import { ActionBarButton, ActionName } from '@/components/ActionsBar/types/ActionBarComponentTypes'
import { DialogEvent } from '@/store/modules/customFields/customFieldsTypes'
import { LabelButtonVariant } from '@/components/forms/buttons/types/ButtonTypes'

const customFieldsModule: string = ModuleNamespaces.CUSTOM_FIELDS
const formsModule = ModuleNamespaces.FORMS

@Component({
  components: {
    FormGeneratorComponent,
    LfActionsBarComponent
  }
})
export default class CustomFieldsFormDialogComponent extends Vue {
  @Prop({
    type: Boolean,
    required: true
  })
  showFormDialog!: boolean

  @Prop({
    type: Array,
    required: true,
    default: () => []
  })
  schema!: []

  @Prop({
    type: Object
  })
  formData!: {}

  @Prop({
    type: String,
    required: true
  })
  typeOfFieldToRenderInForm!: string

  @Getter('checkIfFormIsValid', { namespace: formsModule })
  checkIfFormIsValid: (context: string) => boolean
  @Getter('getModeCustomFieldsFormDialog', { namespace: customFieldsModule })
  getModeCustomFieldsFormDialog: boolean

  @Mutation('CLEAR_CUSTOM_FIELD_FORM_DATA', { namespace: customFieldsModule })
  clearFormData: () => void
  @Mutation('MODE_CUSTOM_FIELDS_FORM_DIALOG', { namespace: customFieldsModule })
  modeCustomFieldsFormDialog: (editMode: boolean) => void

  closeIcon: string = Icons.CLOSE

  context = ContextName.CUSTOM_FIELDS

  @Emit()
  dialogEvent(e: string): DialogEvent {
    return {
      action: e,
      formData: this.formData
    }
  }

  get buttons(): ActionBarButton[] {
    return [
      {
        tooltip: this.$t('action_buttons.cancel'),
        class: 'secondary-color',
        action: ActionName.CLOSE,
        variant: LabelButtonVariant.OUTLINED,
        enabled: true
      },
      {
        tooltip: this.$t('action_buttons.add'),
        class: 'main-color',
        action: ActionName.SAVE,
        hidden: this.getModeCustomFieldsFormDialog,
        enabled: this.checkIfFormIsValid(this.context)
      },
      {
        tooltip: this.$t('action_buttons.save'),
        class: 'main-color',
        action: ActionName.EDIT,
        hidden: !this.getModeCustomFieldsFormDialog,
        enabled: this.checkIfFormIsValid(this.context)
      }
    ]
  }

  closeFormDialog(e: MouseEvent): void {
    const target = e.target as HTMLElement
    const checkClassList: boolean =
      target.classList.contains('v-dialog__content--active') || target.classList.contains('close-icon')
    if (checkClassList) {
      this.dialogEvent(ActionName.CLOSE)
      this.clearForm()
    }
  }

  executeAction(actionName: string): void {
    this.dialogEvent(actionName)
    this.clearForm()
    this.modeCustomFieldsFormDialog(false)
  }

  clearForm(): void {
    this.clearFormData()
  }
}
</script>

<style lang="scss" scoped>
.v-dialog__content {
  background-color: $dialog-background-color;
  pointer-events: unset;
}

::v-deep .v-dialog.v-dialog--active {
  border-radius: 0;
}

::v-deep .lexon-dropdown-list-options {
  flex-direction: column !important;
}

.required-fields-advice {
  @include milano-regular-14;
  display: flex;
  position: relative;
  color: $neutral-grey-600;
  align-self: flex-end;
  padding-right: 16px;
  width: 100%;
  justify-content: flex-end;
  margin-top: $spacing-md;

  &:before {
    @include milano-bold-20;
    display: inline-block;
    position: relative;
    top: -4px;
    content: '*';
    color: $subject-fireEngineRed;
    margin-right: 3px;
  }
}

.form-title-container {
  display: flex;
  min-height: 46px;
  padding: $spacing-xs;
  align-items: center;
  align-self: stretch;
  border-radius: $cornerRadius-sm;
  background: $main-1000;

  .form-title {
    @include milano-bold-18;
    color: $neutral-white-000;
  }
}

.form-content {
  display: flex;
  padding: $spacing-xl;
  flex-direction: column;
  align-items: center;
  border-radius: $cornerRadius-md;
  border: 1px solid $main-200;
  background: $neutral-white-000;
  box-shadow: $shadow-md;

  ::v-deep .form-generator-container {
    width: 100%;

    .form {
      width: 100%;
    }
  }
}

.action-buttons {
  @include flex($justify-content: flex-end);
  width: 100%;
  margin-top: 20px;
}
</style>
